import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Middle Stage Peer Assessment Viewer Component (READ-ONLY)
 * Designed to match the UI in Image 2 (Peer Feedback)
 *
 * @param {Object} submission - The submission object containing peer_assessment data
 */
const MiddleStagePeerAssessmentViewer = ({ submission }) => {
    // If no peer assessment data is available, show placeholder
    if (!submission || !submission.peer_assessment) {
        return (
            <div className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-orange-200">
                <div className="px-4 py-3 bg-orange-100">
                    <h2 className="text-lg font-medium text-center uppercase">Peer Feedback</h2>
                </div>
                <div className="p-6 text-center">
                    <AlertCircle className="h-12 w-12 text-orange-200 mx-auto mb-3" />
                    <p className="text-gray-500">No peer assessment has been submitted yet.</p>
                </div>
            </div>
        );
    }

    // Response options for the experience questions
    const responseOptions = [
        { value: 'Yes', label: 'Yes', emoji: '😊👍' },
        { value: 'Sometimes', label: 'Sometimes', emoji: '😐👉' },
        { value: 'No', label: 'No', emoji: '😔👎' },
        { value: 'Not sure', label: 'Not sure', emoji: '🤔' }
    ];

    // Helper function to check if an option is selected
    const isOptionSelected = (questionKey, optionValue) => {
        return submission.peer_assessment.experience &&
               submission.peer_assessment.experience[questionKey] === optionValue;
    };

    // Helper function to check if a grid item is selected
    const isGridItemSelected = (category, itemText) => {
        return submission.peer_assessment.progress_grid &&
               Array.isArray(submission.peer_assessment.progress_grid[category]) &&
               submission.peer_assessment.progress_grid[category].includes(itemText);
    };

    // Progress grid categories and items
    const progressGrid = {
        awareness: [
            { text: 'My peer learnt something new.' },
            { text: 'My peer was attentive to every detail of the activity.' },
            { text: 'My peer understood the activity.' },
            { text: 'My peer was able to focus on the activity.' },
            { text: 'My peer followed the instructions.' },
            { text: 'My peer found this activity meaningful.' }
        ],
        sensitivity: [
            { text: 'My peer can express his/her emotions well.' },
            { text: 'My peer can understand my emotions well.' },
            { text: 'My peer contributed to the success of the activity.' },
            { text: 'My peer was motivated throughout the activity.' },
            { text: 'My peer was able to ask help/support from me or the teacher.' },
            { text: 'My peer was able to help others in some way.' }
        ],
        creativity: [
            { text: 'My peer was curious to learn new things.' },
            { text: 'My peer was able to think of \'out of the box\' solutions.' },
            { text: 'My peer was able to express her/his creativity during the activity.' },
            { text: 'My peer was able to generate innovative ideas.' },
            { text: 'My peer was able to take calculated risks.' }
        ]
    };

    // Count selected items in each category
    const getSelectedCount = (category) => {
        if (!submission.peer_assessment.progress_grid ||
            !Array.isArray(submission.peer_assessment.progress_grid[category])) {
            return 0;
        }
        return submission.peer_assessment.progress_grid[category].length;
    };

    return (
        <div className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-gray-200">
            {/* Header */}
            <div className="px-4 py-3 bg-orange-100">
                <h2 className="text-lg font-medium text-gray-900 text-center uppercase">
                    Peer Feedback
                </h2>
            </div>

            <div className="p-4">
                {/* Names Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                        <p className="text-sm font-medium text-gray-700">My name is</p>
                        <p className="mt-1">{submission.peer_assessment.experience?.my_name || "Not provided"}</p>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                        <p className="text-sm font-medium text-gray-700">My peer's name is</p>
                        <p className="mt-1">{submission.peer_assessment.experience?.peer_name || "Not provided"}</p>
                    </div>
                </div>

                {/* Experience Questions Section */}
                <div className="mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-4">
                        Based on your experience of the activity, please circle the response that is applicable.
                    </p>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <tbody>
                                {/* Question 1 */}
                                <tr className="divide-x divide-gray-200">
                                    <td className="px-4 py-3 text-sm text-gray-900 w-2/5">
                                        My peer was engaged and motivated during the activity.
                                    </td>
                                    {responseOptions.map((option) => (
                                        <td key={option.value} className={`px-4 py-3 text-center ${isOptionSelected('engaged_and_motivated', option.value) ? 'bg-orange-50' : ''}`}>
                                            <div className="flex flex-col items-center">
                                                <div className={`text-2xl mb-1 ${isOptionSelected('engaged_and_motivated', option.value) ? 'text-orange-500' : 'text-gray-300'}`}>
                                                    {option.emoji}
                                                </div>
                                                <span className="text-xs">{option.label}</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>

                                {/* Question 2 */}
                                <tr className="divide-x divide-gray-200 bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                        My peer effectively shared thoughts and ideas during the activity.
                                    </td>
                                    {responseOptions.map((option) => (
                                        <td key={option.value} className={`px-4 py-3 text-center ${isOptionSelected('shared_thoughts', option.value) ? 'bg-orange-50' : ''}`}>
                                            <div className="flex flex-col items-center">
                                                <div className={`text-2xl mb-1 ${isOptionSelected('shared_thoughts', option.value) ? 'text-orange-500' : 'text-gray-300'}`}>
                                                    {option.emoji}
                                                </div>
                                                <span className="text-xs">{option.label}</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Progress Grid Section */}
                <div className="mb-6">
                    <div className="bg-orange-100 py-2 px-4 text-center mb-4">
                        <h3 className="font-medium text-gray-900">My Peer's Progress Grid</h3>
                    </div>

                    <p className="text-sm text-gray-700 mb-4">
                        Based on your peer's engagement with the activity, circle the statements you think are true for your peer.
                    </p>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr className="divide-x divide-gray-200">
                                    <th scope="col" className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-pink-100 w-1/3">
                                        Awareness
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-green-100 w-1/3">
                                        Sensitivity
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-blue-100 w-1/3">
                                        Creativity
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {/* Create rows based on the maximum number of items in any category */}
                                {Array.from({ length: Math.max(
                                    progressGrid.awareness.length,
                                    progressGrid.sensitivity.length,
                                    progressGrid.creativity.length
                                )}).map((_, rowIndex) => (
                                    <tr key={rowIndex} className={`divide-x divide-gray-200 ${rowIndex % 2 === 1 ? 'bg-gray-50' : ''}`}>
                                        <td className="px-4 py-2">
                                            {progressGrid.awareness[rowIndex] && (
                                                <div className="flex items-start">
                                                    <div className={`flex-shrink-0 h-5 w-5 mt-0.5 ${
                                                        isGridItemSelected('awareness', progressGrid.awareness[rowIndex].text) 
                                                            ? 'text-green-500' 
                                                            : 'text-gray-300'
                                                    }`}>
                                                        {isGridItemSelected('awareness', progressGrid.awareness[rowIndex].text) ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`ml-2 text-xs ${isGridItemSelected('awareness', progressGrid.awareness[rowIndex].text) ? 'font-medium' : ''}`}>
                                                        {progressGrid.awareness[rowIndex].text}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            {progressGrid.sensitivity[rowIndex] && (
                                                <div className="flex items-start">
                                                    <div className={`flex-shrink-0 h-5 w-5 mt-0.5 ${
                                                        isGridItemSelected('sensitivity', progressGrid.sensitivity[rowIndex].text) 
                                                            ? 'text-green-500' 
                                                            : 'text-gray-300'
                                                    }`}>
                                                        {isGridItemSelected('sensitivity', progressGrid.sensitivity[rowIndex].text) ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`ml-2 text-xs ${isGridItemSelected('sensitivity', progressGrid.sensitivity[rowIndex].text) ? 'font-medium' : ''}`}>
                                                        {progressGrid.sensitivity[rowIndex].text}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            {progressGrid.creativity[rowIndex] && (
                                                <div className="flex items-start">
                                                    <div className={`flex-shrink-0 h-5 w-5 mt-0.5 ${
                                                        isGridItemSelected('creativity', progressGrid.creativity[rowIndex].text) 
                                                            ? 'text-green-500' 
                                                            : 'text-gray-300'
                                                    }`}>
                                                        {isGridItemSelected('creativity', progressGrid.creativity[rowIndex].text) ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`ml-2 text-xs ${isGridItemSelected('creativity', progressGrid.creativity[rowIndex].text) ? 'font-medium' : ''}`}>
                                                        {progressGrid.creativity[rowIndex].text}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}

                                {/* Teacher Use Only Row */}
                                <tr className="divide-x divide-gray-200">
                                    <td colSpan="3" className="px-4 py-2 text-sm font-medium text-gray-700 text-center">
                                        (For Teacher's Use Only)
                                    </td>
                                </tr>
                                <tr className="divide-x divide-gray-200 bg-gray-50">
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Awareness: <span className="font-medium">{getSelectedCount('awareness')}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Sensitivity: <span className="font-medium">{getSelectedCount('sensitivity')}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Creativity: <span className="font-medium">{getSelectedCount('creativity')}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Peer Needs Section */}
                <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                            <p className="text-sm font-medium text-gray-700">My peer needs to practice</p>
                            <p className="mt-1">{submission.peer_assessment.learnings?.needs_practice || "Not provided"}</p>
                        </div>

                        <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                            <p className="text-sm font-medium text-gray-700">My peer needs help with</p>
                            <p className="mt-1">{submission.peer_assessment.learnings?.needs_help || "Not provided"}</p>
                        </div>
                    </div>
                </div>

                {/* Teacher Instructions Section */}
                <div className="mb-6 bg-orange-100 p-4 rounded-md">
                    <h3 className="font-medium text-gray-900 text-center mb-3">
                        How to develop a Holistic Progress Summary? (for teacher's use only)
                    </h3>
                    <p className="text-sm text-gray-700 mb-4">
                        Before filling out the Teacher's Feedback Form, kindly calculate the score for the 3 abilities being assessed by the
                        Student's Self Reflection sheet and the Peer Feedback sheet. For each ability i.e. A (Awareness), S (Sensitivity)
                        and C (Creativity), six sub-factors are being assessed (refer appendix). Calculate the number of statements being
                        circled by the student in the self-reflection sheet and by the peer in the peer
                        feedback form. Use the scoring key provided here to assess the student's level of performance on each ability. Indicate the level of performance as reflected on the
                        student and peer assessment sheet in the Student Progress Wheel on the
                        teacher's feedback form.
                    </p>
                    <div className="bg-white p-3 rounded-md border border-gray-300">
                        <h4 className="font-medium text-gray-800 text-center mb-2">Scoring Key:</h4>
                        <p className="text-sm text-gray-700">
                            Beginner – 0,1, 2<br/>
                            Proficient – 3, 4<br/>
                            Advanced – 5, 6
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MiddleStagePeerAssessmentViewer;